import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import ProductGroupSelectDialog from 'components/ProductGroupSelectDialog';
import PromptDialog from 'core/components/Dialogs/PromptDialog';
import SplitButton from 'core/components/SplitButton';
import { intentionallyFloatingPromiseReturn } from 'core/utils/commonUtils';
import { removeGroupById } from 'core/utils/productGroupUtils';
import { isNil } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { useState } from 'react';
import type { ProductGroupSelectedNode } from 'components/ProductGroupSelect/ProductGroupSelect';
import type { ProductGroup } from 'models/product/productGroup';
import type { FC } from 'react';

interface GroupsToolbarProps {
  onAddIn: (name: string) => void;
  onAddOut: (name: string) => void;
  onRename: (name: string) => void;
  onDelete: VoidFunction;
  onMove: (destinationGroupId?: number) => void;
  selectedNode?: ProductGroupSelectedNode;
}

const GroupsToolbar: FC<GroupsToolbarProps> = ({
  onAddIn,
  onAddOut,
  onDelete,
  onMove,
  onRename,
  selectedNode,
}) => {
  const [openMoveDialog, setOpenMoveDialog] = useState(false);

  const [addInNameDialogOpen, setAddInNameDialogOpen] = useState(false);
  const [addOutNameDialogOpen, setAddOutNameDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  const isNodeSelected = (): boolean => !isNil(selectedNode);

  const deleteHandler = async (): Promise<void> => {
    await deletionConfirm({
      description: (
        <>
          <span>Все товары из этой группы и вложенных в нее групп, будут удалены.</span>
          <br />
          <span>Удалить группу?</span>
        </>
      ),
    })
      .then(() => {
        onDelete();
        return true;
      })
      .catch(() => false);
  };

  const deletionConfirm = useConfirm();

  const addInHandler = (): void => {
    setAddInNameDialogOpen(true);
  };

  const addOutHandler = (): void => {
    setAddOutNameDialogOpen(true);
  };

  const handleMoveDialogClose = (groupId?: number): void => {
    if (isNil(groupId)) {
      setOpenMoveDialog(false);
      return;
    }

    onMove(groupId === -1 ? undefined : groupId);
    setOpenMoveDialog(false);
  };

  const addOptions = [
    { label: 'Создать в', action: addInHandler },
    { label: 'Создать рядом', action: addOutHandler },
  ];

  const handleOnCloseAddInNameDialog = (): void => {
    setAddInNameDialogOpen(false);
  };

  const handleOnSaveAddInNameDialog = (value: string): void => {
    onAddIn(value);
    setAddInNameDialogOpen(false);
  };

  const handleOnCloseAddOutNameDialog = (): void => {
    setAddOutNameDialogOpen(false);
  };

  const handleOnSaveAddOutNameDialog = (value: string): void => {
    onAddOut(value);
    setAddOutNameDialogOpen(false);
  };

  const handleOnCloseRenameDialog = (): void => {
    setRenameDialogOpen(false);
  };

  const handleOnSaveRenameDialog = (value: string): void => {
    onRename(value);
    setRenameDialogOpen(false);
  };

  const getGroupsToMove = (data: ProductGroup[]): ProductGroup[] => {
    if (isNil(selectedNode)) {
      return data;
    }

    removeGroupById(data, selectedNode.id);

    return [
      {
        id: -1,
        name: 'Корень',
        groups: data,
      },
    ];
  };

  return (
    <>
      <ProductGroupSelectDialog
        dataPostprocessor={getGroupsToMove}
        open={openMoveDialog}
        onClose={handleMoveDialogClose}
      />

      <PromptDialog
        open={addInNameDialogOpen}
        title={'Введите название группы'}
        onClose={handleOnCloseAddInNameDialog}
        onSave={handleOnSaveAddInNameDialog}
      />

      <PromptDialog
        open={addOutNameDialogOpen}
        title={'Введите название группы'}
        onClose={handleOnCloseAddOutNameDialog}
        onSave={handleOnSaveAddOutNameDialog}
      />

      <PromptDialog
        open={renameDialogOpen}
        defaultValue={selectedNode?.name}
        title={'Введите название группы'}
        onClose={handleOnCloseRenameDialog}
        onSave={handleOnSaveRenameDialog}
      />

      <Stack direction={'row'} alignItems='center' spacing={1}>
        {!isNodeSelected() ? (
          <Button size='small' variant='contained' onClick={addInHandler}>
            Создать
          </Button>
        ) : (
          <>
            <SplitButton
              id='product-groups-options'
              options={addOptions}
              size='small'
              variant='contained'
            />
            <Tooltip title='Переместить выбранную группу'>
              <IconButton
                onClick={() => {
                  setOpenMoveDialog(true);
                }}
                size='small'
                aria-label='move'>
                <DriveFileMoveOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Переименовать выбранную группу'>
              <IconButton
                onClick={() => {
                  setRenameDialogOpen(true);
                }}
                size='small'
                aria-label='rename'>
                <DriveFileRenameOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Удалить выбранную группу'>
              <IconButton
                onClick={intentionallyFloatingPromiseReturn(deleteHandler)}
                size='small'
                aria-label='delete'>
                <DeleteOutlinedIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    </>
  );
};

export default GroupsToolbar;
